import type { SttAuth, SttProviderName } from '@vivport/backend/types';

const STT_SAMPLE_RATE = 16000;
// Pinned to the Universal-Streaming product the voice pricing is based on; the
// provider default (universal-3-5-pro) is a differently priced tier.
const STT_SPEECH_MODEL = 'universal-streaming-english';
const PCM_WORKLET_URL = '/pcm-recorder.worklet.js';
const PCM_WORKLET_PROCESSOR = 'pcm-recorder';
// AssemblyAI v3 turn-detection knobs, passed as socket query params. Names and
// defaults verified against
// https://www.assemblyai.com/docs/streaming/universal-streaming/turn-detection
// (min_turn_silence is the renamed min_end_of_turn_silence_when_confident; some
// third-party wrappers still use the old name). Values are the provider defaults,
// shipped explicit so tuning is a one-line change. Tuning direction: lower the
// confidence threshold / silences for snappier end-of-turn at the cost of
// mid-sentence cuts; raise them for more patient turn-taking.
const STT_END_OF_TURN_CONFIDENCE_THRESHOLD = 0.4;
const STT_MIN_TURN_SILENCE_MS = 400;
const STT_MAX_TURN_SILENCE_MS = 1280;
const STT_VAD_THRESHOLD = 0.4;
// AssemblyAI answers Terminate with a Termination message once its buffer is flushed.
const ASSEMBLYAI_TERMINATION_TIMEOUT_MS = 1200;
// Deepgram Flux (Australian endpoint, /v2/listen). Names and defaults verified against
// https://developers.deepgram.com/docs/flux/configuration.
const DEEPGRAM_MODEL = 'flux-general-en';
const DEEPGRAM_ENCODING = 'linear16';
// Confidence required to fire EndOfTurn; provider default, range 0.5–1.0. Raise for
// fewer false end-of-turns at more latency; lower for snappier turns with more false ends.
const DEEPGRAM_EOT_THRESHOLD = 0.7;
// Silence after which EndOfTurn is forced regardless of confidence; provider default,
// range 500–60000. Lower forces the end sooner after silence; raise for members who pause.
const DEEPGRAM_EOT_TIMEOUT_MS = 5000;
// eager_eot_threshold is deliberately unset: eager end-of-turn multiplies LLM calls,
// the wrong trade for Neo.
// Flux decodes buffered audio after CloseStream and may finish with Update frames and a
// socket close rather than an EndOfTurn, so the flush waits for the close.
const DEEPGRAM_CLOSE_TIMEOUT_MS = 5000;
const ANALYSER_FFT_SIZE = 1024;
// The socket is only usable once OPEN — the worklet drops every frame it produces before
// then, so returning a handle early silently loses the member's first words.
const STT_SOCKET_OPEN_TIMEOUT_MS = 8000;

export interface SttTurn {
  transcript: string;
  endOfTurn: boolean;
}

export type SttStreamProvider = SttProviderName;

export interface OpenSttStreamOptions {
  stream: MediaStream;
  context: AudioContext;
  provider: SttStreamProvider;
  auth: SttAuth;
  token: string;
  socketUrl: string;
  onTurn(turn: SttTurn): void;
  onError(): void;
  onBegin(): void;
}

export interface SttStreamHandle {
  analyser: AnalyserNode;
  stop(): Promise<void>;
  teardown(): void;
}

interface SttServerMessage {
  type: string;
  transcript?: string;
  end_of_turn?: boolean;
  event?: string;
}

export function detectStreamingSupport(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof WebSocket !== 'undefined' &&
    typeof AudioWorkletNode !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

function buildSocketUrl(options: OpenSttStreamOptions): string {
  switch (options.provider) {
    case 'assemblyai': {
      const params = new URLSearchParams({
        token: options.token,
        sample_rate: String(STT_SAMPLE_RATE),
        encoding: 'pcm_s16le',
        speech_model: STT_SPEECH_MODEL,
        end_of_turn_confidence_threshold: String(STT_END_OF_TURN_CONFIDENCE_THRESHOLD),
        min_turn_silence: String(STT_MIN_TURN_SILENCE_MS),
        max_turn_silence: String(STT_MAX_TURN_SILENCE_MS),
        vad_threshold: String(STT_VAD_THRESHOLD),
      });
      return `${options.socketUrl}?${params.toString()}`;
    }
    case 'deepgram': {
      const params = new URLSearchParams({
        model: DEEPGRAM_MODEL,
        encoding: DEEPGRAM_ENCODING,
        sample_rate: String(STT_SAMPLE_RATE),
        eot_threshold: String(DEEPGRAM_EOT_THRESHOLD),
        eot_timeout_ms: String(DEEPGRAM_EOT_TIMEOUT_MS),
      });
      return `${options.socketUrl}?${params.toString()}`;
    }
  }
}

export async function openSttStream(options: OpenSttStreamOptions): Promise<SttStreamHandle> {
  const { stream, context, provider, onTurn, onError, onBegin } = options;
  const closeMessage = provider === 'deepgram' ? { type: 'CloseStream' } : { type: 'Terminate' };
  const flushTimeoutMs = provider === 'deepgram' ? DEEPGRAM_CLOSE_TIMEOUT_MS : ASSEMBLYAI_TERMINATION_TIMEOUT_MS;
  await context.audioWorklet.addModule(PCM_WORKLET_URL);

  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = ANALYSER_FFT_SIZE;
  source.connect(analyser);
  const worklet = new AudioWorkletNode(context, PCM_WORKLET_PROCESSOR);
  source.connect(worklet);
  // The worklet writes no output samples; connecting keeps the graph pulled.
  worklet.connect(context.destination);

  const url = buildSocketUrl(options);
  const ws = options.auth === 'subprotocol' ? new WebSocket(url, ['bearer', options.token]) : new WebSocket(url);

  // Wait for OPEN before returning. The rejection carries no detail on purpose: the
  // credential lives in the socket URL's query string or the subprotocol pair, and this
  // Error is logged by the callers.
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => settle(new Error('stt socket did not open')), STT_SOCKET_OPEN_TIMEOUT_MS);
    function settle(err: Error | null): void {
      clearTimeout(timer);
      ws.onopen = null;
      ws.onerror = null;
      ws.onclose = null;
      if (err === null) {
        resolve();
        return;
      }
      if (ws.readyState !== WebSocket.CLOSING && ws.readyState !== WebSocket.CLOSED) {
        ws.close();
      }
      reject(err);
    }
    ws.onopen = () => settle(null);
    ws.onerror = () => settle(new Error('stt socket did not open'));
    ws.onclose = () => settle(new Error('stt socket did not open'));
  });

  let tornDown = false;
  let stopping = false;
  let begun = false;
  let settleStop: (() => void) | null = null;

  worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(event.data);
    }
  };

  const pagehideHandler = () => {
    // sendBeacon cannot carry the bearer-token + member-id header auth, so no end
    // call is possible on tab close; the backend voice-session reaper is the
    // designed catch-all for sessions abandoned here.
    teardown();
  };

  function teardown(): void {
    if (tornDown) return;
    tornDown = true;
    settleStop?.();
    settleStop = null;
    window.removeEventListener('pagehide', pagehideHandler);
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(closeMessage));
    }
    if (ws.readyState !== WebSocket.CLOSING && ws.readyState !== WebSocket.CLOSED) {
      ws.close();
    }
    worklet.port.onmessage = null;
    worklet.disconnect();
    analyser.disconnect();
    source.disconnect();
  }

  async function stop(): Promise<void> {
    if (tornDown) return;
    stopping = true;
    const terminated = new Promise<void>((resolve) => {
      settleStop = resolve;
    });
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(closeMessage));
    }
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, flushTimeoutMs));
    await Promise.race([terminated, timeout]);
    teardown();
  }

  ws.onmessage = (event) => {
    if (typeof event.data !== 'string') return;
    const message = JSON.parse(event.data) as SttServerMessage;
    if (provider === 'deepgram') {
      // Flux sends no Begin; the first frame of any kind proves the socket is live.
      if (!begun) {
        begun = true;
        onBegin();
      }
      if (message.type !== 'TurnInfo') return;
      if (message.event === 'Update') {
        onTurn({ transcript: message.transcript ?? '', endOfTurn: false });
      } else if (message.event === 'EndOfTurn') {
        onTurn({ transcript: message.transcript ?? '', endOfTurn: true });
      }
      return;
    }
    if (message.type === 'Begin') {
      onBegin();
      return;
    }
    if (message.type === 'Turn') {
      onTurn({ transcript: message.transcript ?? '', endOfTurn: message.end_of_turn === true });
      return;
    }
    if (message.type === 'Termination') {
      settleStop?.();
      settleStop = null;
    }
  };
  ws.onerror = () => {
    if (stopping) {
      settleStop?.();
      settleStop = null;
      return;
    }
    if (!tornDown) onError();
  };
  ws.onclose = () => {
    if (stopping) {
      settleStop?.();
      settleStop = null;
      return;
    }
    if (!tornDown) onError();
  };

  window.addEventListener('pagehide', pagehideHandler);

  return { analyser, stop, teardown };
}
